<template>
  <div class="space-y-4">
    <div v-if="!canView" class="bg-white rounded-lg shadow p-8 text-center">
      <i class="pi pi-lock text-4xl text-gray-300 mb-3"></i>
      <p class="text-gray-500">You do not have permission to view transcript requests.</p>
    </div>

    <template v-else>
      <!-- Public transcript link (copyable) for the current school -->
      <PublicTranscriptLink />

      <!-- Sub-tabs -->
      <div class="flex items-center gap-1 border-b">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          type="button"
          class="px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors"
          :class="activeTab === tab.value
            ? 'border-primary text-primary'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
          :data-testid="`transcript-tab-${tab.value}`"
          @click="onTabChange(tab.value)"
        >
          <i :class="['pi', tab.icon, 'mr-1']"></i>
          {{ tab.label }}
        </button>
      </div>

      <!-- Requests view (with master-detail) -->
      <div v-if="activeTab === 'requests'">
        <div v-if="!selectedRequestId">
          <TranscriptRequestsList ref="listRef" @view="onViewRequest" />
        </div>
        <div v-else>
          <Button
            icon="pi pi-arrow-left"
            label="Back to list"
            severity="secondary"
            text
            size="small"
            class="mb-3"
            data-testid="transcript-back-to-list"
            @click="selectedRequestId = null"
          />
          <TranscriptRequestDetail :requestId="selectedRequestId" />
        </div>
      </div>

      <!-- Generate view -->
      <div v-else-if="activeTab === 'generate'">
        <AdminGenerateTranscript />
      </div>

      <!-- Types view -->
      <div v-else-if="activeTab === 'types'">
        <TranscriptTypesList />
      </div>

      <!-- Templates view -->
      <div v-else-if="activeTab === 'templates'">
        <TranscriptTemplatesList />
      </div>

      <!-- Matric Lookups view -->
      <div v-else-if="activeTab === 'matric'">
        <MatricRetrievalAttemptsList />
      </div>

      <!-- PDF Settings view -->
      <div v-else-if="activeTab === 'settings'">
        <PdfTranscriptConfig />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/store/userStore';
import { can, AppPermissions } from '@/utils/permissions';
import AdminGenerateTranscript from './AdminGenerateTranscript.vue';
import TranscriptTemplatesList from './TranscriptTemplatesList.vue';
import PdfTranscriptConfig from './PdfTranscriptConfig.vue';
import MatricRetrievalAttemptsList from './MatricRetrievalAttemptsList.vue';

type TabValue = 'requests' | 'generate' | 'types' | 'templates' | 'matric' | 'settings';

const { currentUser } = storeToRefs(useUserStore());

const canView = computed(() =>
  can(currentUser.value, AppPermissions.Transcripts.Actions.View, AppPermissions.Transcripts.Resource),
);

const activeTab = ref<TabValue>('requests');
const selectedRequestId = ref<string | null>(null);
const listRef = ref<{ refresh: () => void } | null>(null);

const canGenerate = computed(() =>
  can(currentUser.value, AppPermissions.Transcripts.Actions.Generate, AppPermissions.Transcripts.Resource),
);

const tabs = computed(() => {
  const items: { value: TabValue; label: string; icon: string }[] = [
    { value: 'requests', label: 'Requests', icon: 'pi-inbox' },
  ];
  if (canGenerate.value) {
    items.push({ value: 'generate', label: 'Generate', icon: 'pi-file' });
  }
  if (can(currentUser.value, AppPermissions.Transcripts.Actions.Create, AppPermissions.Transcripts.Resource)
    || can(currentUser.value, AppPermissions.Transcripts.Actions.Update, AppPermissions.Transcripts.Resource)) {
    items.push({ value: 'types', label: 'Types', icon: 'pi-cog' });
  }
  items.push({ value: 'templates', label: 'Templates', icon: 'pi-palette' });
  items.push({ value: 'matric', label: 'Matric Lookups', icon: 'pi-search' });
  items.push({ value: 'settings', label: 'PDF Settings', icon: 'pi-sliders-h' });
  return items;
});

const onTabChange = (next: TabValue) => {
  activeTab.value = next;
  selectedRequestId.value = null;
};

const onViewRequest = (id: string) => {
  selectedRequestId.value = id;
};
</script>
